import * as t from "io-ts";
import { GlobalColorV } from "@/types/colors";
import { UnitInputValue } from "@/types/unit";
import { PropertiesDataV } from "@/types/tree";
import { FontV } from "@/types/fonts";
import { BreakpointV } from "@/types/breakpoints";
import { DesignLibraryDataV, DesignProviderV } from "@/types/design-library";

export type OnboardingWebsiteDetails = t.TypeOf<
  typeof OnboardingWebsiteDetailsV
>;

export const MultipleChoiceOptionV = t.type({
  id: t.number,
  label: t.string,
  selected: t.boolean
});

export type MultipleChoiceOption = t.TypeOf<typeof MultipleChoiceOptionV>;

export const OnboardingWebsiteDetailsV = t.type({
  websiteName: t.string,
  websiteDescription: t.string,
  websiteCategory: t.array(MultipleChoiceOptionV),
  websiteGoals: t.array(MultipleChoiceOptionV),
  websiteWritingStyle: t.array(MultipleChoiceOptionV)
});

export const WebsiteCategoriesV = t
  .array(MultipleChoiceOptionV)
  .encode(
    [
      "Portfolio",
      "Event",
      "Business",
      "Restaurant",
      "E-Commerce",
      "Personal Brand",
      "Blog",
      "Non-Profit",
      "Landing Page",
      "Startup"
    ].map((label, i) => ({ id: Number(i + 1), label, selected: false }))
  );

export type WebsiteCategory = (typeof WebsiteCategoriesV)[number];

export const WebsiteGoalsV = t
  .array(MultipleChoiceOptionV)
  .encode(
    [
      "Showcase work",
      "Build a community",
      "Sell products online",
      "Provide information",
      "Generate leads",
      "Book appointments",
      "Share updates",
      "Drive sign-ups",
      "Promote events",
      "Raise donations"
    ].map((label, i) => ({ id: Number(i + 1), label, selected: false }))
  );

export type WebsiteGoal = (typeof WebsiteGoalsV)[number];

export const WebsiteWritingStyleV = t
  .array(MultipleChoiceOptionV)
  .encode(
    [
      "Conversational",
      "Formal",
      "Informative",
      "Persuasive",
      "Storytelling",
      "Concise",
      "Technical",
      "Inspirational",
      "Friendly",
      "Authoritative",
      "Casual",
      "Witty",
      "Educational",
      "Direct",
      "Descriptive",
      "Engaging"
    ].map((label, i) => ({ id: Number(i + 1), label, selected: false }))
  );

export type WebsiteWritingStyle = (typeof WebsiteWritingStyleV)[number];

export const ColorScaleV = t.type({
  100: GlobalColorV,
  200: GlobalColorV,
  300: GlobalColorV,
  400: GlobalColorV,
  500: GlobalColorV,
  600: GlobalColorV,
  700: GlobalColorV,
  800: GlobalColorV,
  900: GlobalColorV
});

export const NamedColorPaletteV = t.type({
  label: t.string,
  primary: t.boolean,
  locked: t.boolean,
  base: GlobalColorV,
  scale: ColorScaleV,
  text: GlobalColorV
});

export const MessageDataV = t.union([
  t.type({
    type: t.literal("updateGlobalSettingsPerPath"),
    payload: t.type({
      path: t.string,
      value: PropertiesDataV
    })
  }),
  t.type({
    type: t.literal("updateBreakpoint"),
    payload: t.type({
      breakpoint: BreakpointV
    })
  }),
  t.type({
    type: t.literal("hideHomepageSections"),
    payload: t.type({
      nodeIds: t.array(t.number)
    })
  })
]);

export type MessageData = t.TypeOf<typeof MessageDataV>;

export type NamedColorPalette = t.TypeOf<typeof NamedColorPaletteV>;
export type ColorScale = t.TypeOf<typeof ColorScaleV>;

export const BORDER_RADIUS_VALUES = {
  none: {
    value: 0,
    unit: "px",
    style: "0px"
  } as unknown as UnitInputValue,
  sm: {
    value: 8,
    unit: "px",
    style: "8px"
  } as unknown as UnitInputValue,
  md: {
    value: 16,
    unit: "px",
    style: "16px"
  } as unknown as UnitInputValue,
  lg: {
    value: 24,
    unit: "px",
    style: "24px"
  } as unknown as UnitInputValue,
  full: {
    value: 9999,
    unit: "px",
    style: "9999px"
  } as unknown as UnitInputValue
};

export const BorderRadiusV = t.keyof(BORDER_RADIUS_VALUES);

export type BorderRadius = t.TypeOf<typeof BorderRadiusV>;

export const OnboardingType = t.union([
  t.literal("barebones"),
  t.literal("template")
]);

export const WebsiteColorPalettesV = t.type({
  neutral: t.union([NamedColorPaletteV, t.null]),
  primary: t.union([NamedColorPaletteV, t.null]),
  secondary: t.array(NamedColorPaletteV)
});

export const OnboardingButtonsV = t.type({
  radius: BorderRadiusV
});
export type OnboardingButtons = t.TypeOf<typeof OnboardingButtonsV>;

export const OnboaridngImagesV = t.type({
  radius: BorderRadiusV
});
export type OnboardingImages = t.TypeOf<typeof OnboaridngImagesV>;

export type WebsiteColorPalettes = t.TypeOf<typeof WebsiteColorPalettesV>;

export const OnboardingTypographyV = t.type({
  headings: t.union([FontV, t.null]),
  body: t.union([FontV, t.null])
});

export type OnboardingTypography = t.TypeOf<typeof OnboardingTypographyV>;

export const OnboardingWebsiteUIV = t.type({
  colorPalettes: WebsiteColorPalettesV,
  typography: OnboardingTypographyV,
  buttons: OnboardingButtonsV,
  images: OnboaridngImagesV
});

export type OnboardingWebsiteUI = t.TypeOf<typeof OnboardingWebsiteUIV>;

export const OnboardingGeneratedPageV = t.type({
  postId: t.number,
  postTitle: t.string
});

export type OnboardingGeneratedPage = t.TypeOf<typeof OnboardingGeneratedPageV>;

export const OnboardingGeneratedPagesSelectedV = t.type({
  homepageSectionIds: t.union([t.array(t.number), t.null]),
  pageIds: t.union([t.array(t.number), t.null])
});

export type OnboardingGeneratedPagesSelected = t.TypeOf<
  typeof OnboardingGeneratedPagesSelectedV
>;

export const OnboardingGeneratedPagesV = t.type({
  homepage: t.union([OnboardingGeneratedPageV, t.null]),
  pages: t.array(OnboardingGeneratedPageV),
  toRemove: OnboardingGeneratedPagesSelectedV
});

export type OnboardingGeneratedPages = t.TypeOf<
  typeof OnboardingGeneratedPagesV
>;

export const DesignLibraryV = t.type({
  selectedProvider: t.union([DesignProviderV, t.null]),
  data: DesignLibraryDataV
});

export type DesignLibrary = t.TypeOf<typeof DesignLibraryV>;

export const OnboardingDataV = t.type({
  websiteDetails: OnboardingWebsiteDetailsV,
  type: OnboardingType,
  websiteUI: OnboardingWebsiteUIV,
  generatedPages: OnboardingGeneratedPagesV,
  previewBreakpointId: t.string,
  designLibrary: DesignLibraryV
});

export type OnboardingData = t.TypeOf<typeof OnboardingDataV>;
